import { motion } from 'framer-motion';
import { Heart, Mail, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import type { AnimationThemeConfig } from '@/types/animationThemes';

const copy = {
  en: { from: 'From', received: 'You received a', title: 'Living Announcement', subtitle: 'from someone who cares about you.', open: 'Open Message', brand: 'Say It NOW • While They\'re Still Here', defaultSender: 'Someone who cares' },
  es: { from: 'De', received: 'Recibiste un', title: 'Anuncio Viviente', subtitle: 'de alguien que se preocupa por ti.', open: 'Abrir Mensaje', brand: 'Say It NOW • Mientras Aún Están Aquí', defaultSender: 'Alguien que se preocupa' },
  pt: { from: 'De', received: 'Você recebeu um', title: 'Anúncio Vivo', subtitle: 'de alguém que se importa com você.', open: 'Abrir Mensagem', brand: 'Say It NOW • Enquanto Ainda Estão Aqui', defaultSender: 'Alguém que se importa' },
  fr: { from: 'De', received: 'Vous avez reçu un', title: 'Annonce Vivante', subtitle: 'de quelqu\'un qui tient à vous.', open: 'Ouvrir le Message', brand: 'Say It NOW • Tant Qu\'ils Sont Encore Là', defaultSender: 'Quelqu\'un qui tient à vous' },
  ar: { from: 'من', received: 'لقد تلقيت', title: 'إعلان حي', subtitle: 'من شخص يهتم بك.', open: 'افتح الرسالة', brand: 'Say It NOW • بينما لا يزالون هنا', defaultSender: 'شخص يهتم بك' },
  ja: { from: '送信者', received: 'あなたに届きました', title: 'リビングアナウンスメント', subtitle: 'あなたを大切に思う人から。', open: 'メッセージを開く', brand: 'Say It NOW • まだここにいるうちに', defaultSender: 'あなたを大切に思う人' },
  ko: { from: '보낸 사람', received: '당신에게 도착했습니다', title: '리빙 어나운스먼트', subtitle: '당신을 아끼는 사람으로부터.', open: '메시지 열기', brand: 'Say It NOW • 아직 곁에 있을 때', defaultSender: '당신을 아끼는 사람' },
  id: { from: 'Dari', received: 'Anda menerima sebuah', title: 'Pengumuman Hidup', subtitle: 'dari seseorang yang peduli pada Anda.', open: 'Buka Pesan', brand: 'Say It NOW • Selagi Mereka Masih Ada', defaultSender: 'Seseorang yang peduli' },

  hi: { from: 'से', received: 'आपको एक प्राप्त हुआ है', title: 'Living Announcement', subtitle: 'किसी ऐसे व्यक्ति से जो आपकी परवाह करता है।', open: 'संदेश खोलें', brand: 'Say It NOW • जब तक वे अभी भी यहाँ हैं', defaultSender: 'कोई जो परवाह करता है' },
};

function FloralBorder({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 200" className={cn('absolute pointer-events-none', className)} fill="none">
      <path d="M20 20 Q40 10 60 20 Q80 30 100 20 Q120 10 140 20 Q160 30 180 20" stroke="currentColor" strokeWidth="1" opacity="0.3" />
      <circle cx="30" cy="25" r="4" fill="currentColor" opacity="0.15" />
      <circle cx="100" cy="15" r="3" fill="currentColor" opacity="0.2" />
      <circle cx="170" cy="25" r="4" fill="currentColor" opacity="0.15" />
      <ellipse cx="60" cy="18" rx="3" ry="6" fill="currentColor" opacity="0.1" transform="rotate(-20 60 18)" />
      <ellipse cx="140" cy="18" rx="3" ry="6" fill="currentColor" opacity="0.1" transform="rotate(20 140 18)" />
    </svg>
  );
}

function SealIcon({ theme }: { theme: AnimationThemeConfig }) {
  const colors: Record<string, string> = {
    rose: 'text-rose-400',
    amber: 'text-amber-500',
    emerald: 'text-emerald-500',
    stone: 'text-stone-500',
    violet: 'text-violet-400',
  };
  const color = colors[theme.accentColor] || 'text-primary';

  if (theme.openingAnimation === 'envelope') return <Mail className={cn('h-10 w-10', color)} />;
  if (theme.openingAnimation === 'light_reveal') return <Sparkles className={cn('h-10 w-10', color)} />;
  return <Heart className={cn('h-10 w-10', color)} fill="currentColor" />;
}

interface ClosedCardProps {
  theme: AnimationThemeConfig;
  recipientName: string;
  senderLabel?: string;
  onOpen: () => void;
}

export function ClosedCard({ theme, recipientName, senderLabel, onOpen }: ClosedCardProps) {
  const { language } = useLanguage();
  const t = lc(copy, language);

  const displaySender = senderLabel || t.defaultSender;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-muted/30 to-background">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-w-md w-full"
      >
        {/* Outer glow */}
        <div className={cn(
          'absolute -inset-4 rounded-3xl blur-2xl opacity-30',
          `bg-gradient-to-br ${theme.closedCardStyle}`
        )} />

        {/* Card */}
        <div className={cn(
          'relative rounded-2xl border-2 overflow-hidden',
          'bg-gradient-to-br backdrop-blur-sm shadow-2xl',
          theme.closedCardStyle,
          theme.borderStyle,
        )}>
          <FloralBorder className="w-full h-8 top-2 left-0 text-current opacity-40" />

          {/* Noise texture */}
          <div
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            }}
          />

          <div className="relative z-10 flex flex-col items-center text-center px-5 sm:px-8 py-14 md:py-20">
            {/* Sender label */}
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-sm font-medium text-foreground/80 mb-6 tracking-wide"
            >
              {t.from}: <span className="font-semibold text-foreground">{displaySender}</span>
            </motion.p>

            {/* Animated seal/icon */}
            <motion.div
              animate={{ scale: [1, 1.05, 1], rotate: [0, 2, -2, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="mb-8"
            >
              <div className={cn(
                'h-20 w-20 rounded-full flex items-center justify-center',
                'bg-white/60 dark:bg-white/10 shadow-lg border',
                theme.borderStyle,
              )}>
                <SealIcon theme={theme} />
              </div>
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-3 inline-flex items-center rounded-full border border-border/50 bg-background/70 px-4 py-1.5 font-serif text-xl md:text-2xl font-medium tracking-wide text-foreground shadow-sm backdrop-blur-sm"
            >
              {t.received}
            </motion.h2>
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="font-heading text-2xl sm:text-3xl md:text-4xl text-foreground mb-4 billboard-flash leading-tight"
            >
              {t.title}
            </motion.h1>

            {/* Divider */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.7, duration: 0.5 }}
              className="w-24 h-px bg-foreground/30 mb-6"
            />

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="text-muted-foreground text-sm md:text-base mb-10 max-w-xs leading-relaxed"
            >
              {t.subtitle}
            </motion.p>

            {/* Open button */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
            >
              <Button
                onClick={onOpen}
                size="lg"
                className={cn(
                  'rounded-full px-10 py-6 text-lg font-medium shadow-xl',
                  'hover:shadow-2xl hover:scale-105 active:scale-95',
                  'transition-all duration-300',
                )}
              >
                <Heart className="h-5 w-5 mr-2" />
                {t.open}
              </Button>
            </motion.div>

            {/* Branding footer — high contrast */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
              className="text-sm font-semibold text-foreground/90 dark:text-foreground/80 mt-8 tracking-wider"
              style={{ textShadow: '0 1px 4px hsl(var(--background) / 0.7)' }}
            >
              {t.brand}
            </motion.p>
          </div>

          <FloralBorder className="w-full h-8 bottom-2 left-0 text-current opacity-40 rotate-180" />
        </div>
      </motion.div>
    </div>
  );
}
